<template>
  <section class="contact-opportunities">
    <woot-tabs :index="0">
      <woot-tabs-item
        :name="$t('CONTACT_PANEL.OPPORTUNITIES.TITLE')"
        :count="totalCount"
        :show-badge="totalCount > 0"
      />
    </woot-tabs>
    <div class="contact-opportunities__body">
      <opportunity-summary-list
        variant="card"
        :opportunities="opportunities"
        :is-loading="isFetching"
        :has-error="hasError"
        :empty-message="
          $t('CONTACT_PANEL.OPPORTUNITIES.NO_RECORDS_FOR_CONTACT')
        "
      />
      <woot-button
        v-if="hasMore"
        class="contact-opportunities__load-more"
        variant="smooth"
        size="small"
        is-expanded
        :is-loading="isLoadingMore"
        @click="loadMore"
      >
        {{ $t('CONTACT_PANEL.OPPORTUNITIES.LOAD_MORE') }}
      </woot-button>
    </div>
  </section>
</template>

<script>
import alertMixin from 'shared/mixins/alertMixin';
import OpportunitySummaryList from '../../opportunities/components/OpportunitySummaryList';

export default {
  components: {
    OpportunitySummaryList,
  },
  mixins: [alertMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      opportunities: [],
      totalCount: 0,
      currentPage: 0,
      isFetching: false,
      isLoadingMore: false,
      hasError: false,
    };
  },
  computed: {
    hasMore() {
      return (
        !this.isFetching &&
        !this.hasError &&
        this.opportunities.length < this.totalCount
      );
    },
  },
  watch: {
    contactId(newContactId, prevContactId) {
      if (newContactId && newContactId !== prevContactId) {
        this.fetchOpportunities();
      }
    },
  },
  mounted() {
    this.fetchOpportunities();
  },
  methods: {
    fetchPage(page) {
      return this.$store.dispatch('opportunities/listByContact', {
        contactId: this.contactId,
        page,
      });
    },
    async fetchOpportunities() {
      const requestedContactId = this.contactId;

      this.isFetching = true;
      this.isLoadingMore = false;
      this.hasError = false;
      try {
        const { meta, payload } = await this.fetchPage(1);
        if (requestedContactId !== this.contactId) return;
        this.opportunities = payload;
        this.totalCount = meta.count;
        this.currentPage = meta.current_page;
      } catch (error) {
        if (requestedContactId !== this.contactId) return;
        this.opportunities = [];
        this.totalCount = 0;
        this.currentPage = 0;
        this.hasError = true;
      } finally {
        if (requestedContactId === this.contactId) {
          this.isFetching = false;
        }
      }
    },
    async loadMore() {
      if (this.isLoadingMore || !this.hasMore) return;

      const requestedContactId = this.contactId;
      this.isLoadingMore = true;
      try {
        const { meta, payload } = await this.fetchPage(this.currentPage + 1);
        if (requestedContactId !== this.contactId) return;
        const loadedIds = new Set(this.opportunities.map(({ id }) => id));
        this.opportunities = [
          ...this.opportunities,
          ...payload.filter(({ id }) => !loadedIds.has(id)),
        ];
        this.totalCount = payload.length
          ? meta.count
          : this.opportunities.length;
        this.currentPage = meta.current_page;
      } catch (error) {
        if (requestedContactId === this.contactId) {
          this.showAlert(
            this.$t('CONTACT_PANEL.OPPORTUNITIES.LOAD_MORE_ERROR')
          );
        }
      } finally {
        if (requestedContactId === this.contactId) {
          this.isLoadingMore = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.contact-opportunities {
  display: flex;
  flex-direction: column;
  font-size: var(--font-size-small);
  min-height: 0;
}

.contact-opportunities ::v-deep .tabs-title .badge {
  line-height: 1;
  padding-bottom: var(--space-micro);
  padding-top: var(--space-micro);
}

.contact-opportunities__body {
  background: var(--color-background-light);
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: var(--space-normal);
}

.contact-opportunities__load-more {
  margin-top: var(--space-normal);
}
</style>
